import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Minus, ShoppingCart } from 'lucide-react';
import { Product } from '../types';
import { useStore } from '../context/StoreContext';
import { TRANSLATIONS } from '../constants';

export const ProductCard = ({ product }: { product: Product }) => {
  const { addToCart, removeFromCart, updateQuantity, language, cart } = useStore();
  const navigate = useNavigate();
  
  // Use slug for SEO-friendly URLs, fallback to ID
  const productUrl = product.slug ? `/product/${product.slug}` : `/product/${product.id}`;
  
  // Check if product is in cart and get quantity
  const cartItem = cart.find(item => item.id === product.id);
  const quantityInCart = cartItem?.quantity || 0;
  const minQty = product.minQuantity || 6;
  
  const handleDecrease = () => {
    if (quantityInCart <= minQty) {
      removeFromCart(product.id);
    } else {
      updateQuantity(product.id, quantityInCart - minQty);
    }
  };
  
  return (
    <article className="group bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow" role="listitem" itemScope itemType="https://schema.org/Product">
      <Link to={productUrl} className="block relative aspect-square overflow-hidden bg-gray-100">
        <img 
          src={product.image} 
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          itemProp="image"
          loading="lazy"
        />
      </Link>
      <div className="p-4">
        <Link to={productUrl}>
          <h3 className="font-medium text-gray-900 mb-1 truncate hover:text-emerald-600 transition-colors" itemProp="name">{product.name}</h3>
        </Link>
        <p className="text-sm text-gray-500 line-clamp-2 mb-4 h-10" itemProp="description">{product.description}</p>
        <div className="flex items-center justify-between">
          <span className="font-bold text-lg text-gray-900" itemProp="offers" itemScope itemType="https://schema.org/Offer">
            <span itemProp="price">{product.price.toFixed(2)}</span> <span itemProp="priceCurrency" content="PLN">zł</span>
            <meta itemProp="availability" content="https://schema.org/InStock" />
          </span>
          <div className="flex items-center gap-2">
            {quantityInCart > 0 && (
              <button 
                onClick={handleDecrease}
                className="p-2 bg-gray-100 text-gray-900 rounded-full hover:bg-red-500 hover:text-white transition-all active:scale-95"
                title={quantityInCart <= minQty ? TRANSLATIONS.remove[language] : "Decrease quantity"}
                aria-label={`${quantityInCart <= minQty ? TRANSLATIONS.remove[language] : "Decrease quantity"} - ${product.name}`}
              >
                <Minus className="w-5 h-5" />
              </button>
            )}
            <button 
              onClick={() => addToCart(product)}
              className="p-2 bg-gray-100 text-gray-900 rounded-full hover:bg-emerald-600 hover:text-white transition-all active:scale-95"
              title={TRANSLATIONS.addToCart[language]}
              aria-label={`${TRANSLATIONS.addToCart[language]} - ${product.name}`}
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Show quantity and quick checkout when product is in cart */}
        {quantityInCart > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100 flex flex-col gap-2">
            <span className="text-sm font-medium text-emerald-600">
              {TRANSLATIONS.inCart[language]}: {quantityInCart}
            </span>
            <button
              onClick={() => navigate('/checkout')}
              className="flex items-center justify-center gap-1 px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 transition-colors active:scale-95"
              title={TRANSLATIONS.goToCheckout[language]}
            >
              <ShoppingCart className="w-4 h-4" />
              <span>{TRANSLATIONS.checkout[language]}</span>
            </button>
          </div>
        )}
      </div>
    </article>
  );
};